import { SCENES } from '../config/constants.js';
import { t } from './textos.js';

/**
 * Regras puras do fluxo de entrada (Login → Aguarde → fases). Ficam fora das
 * cenas para serem testáveis sem Phaser.
 */

/** Fases que o facilitador libera, com a cena que cada uma abre. */
export const FASES = Object.freeze({
    scanpixel: { tecla: 'S', nome: 'Scan Pixel', cena: SCENES.GAME },
    player1: { tecla: 'P', nome: 'Player 1', cena: SCENES.PLAYER1 },
    labpixels: { tecla: 'L', nome: 'Laboratório de Pixels', cena: SCENES.QUIZ },
    versus: { tecla: 'V', nome: 'Modo Versus', cena: SCENES.VERSUS },
    menuresolucao: { tecla: 'M', nome: 'Menu Resolução', cena: SCENES.MENU_RESOLUCAO }
});

/**
 * Fase aberta que este tablet ainda não concluiu: é para ela que o Aguarde oferece "Voltar para a
 * atividade", qualquer que seja a fase. `liberadas` são as fases entregues ao tablet; as de grupo só
 * contam enquanto o grupo está iniciado ou pausado. Entre várias, vale a primeira na ordem do fluxo.
 */
export function faseEmAndamento ({ liberadas = [], estados = {}, concluidas = [] } = {}) {
    const abertas = new Set(liberadas);
    return Object.keys(FASES).find((fase) => {
        if (!abertas.has(fase) || concluidas.includes(fase)) return false;
        const estado = estados[fase];
        // Fase de grupo (sem estado conhecido = ainda não iniciada para este grupo): só com o grupo em curso.
        return !(fase in estados) || estado === 'iniciado' || estado === 'pausado';
    }) ?? null;
}

/** `RN-CRE-02` / `RI-CRE-01` / `RN-AP-001`: só vincula com o ID (e o código, se exigido) preenchido. */
export function podeVincular (id, { exigeCodigo = false, codigo = '', exigeTurma = false, turma = '' } = {}) {
    const preenchido = (valor) => String(valor ?? '').trim().length > 0;
    return preenchido(id) && (!exigeCodigo || preenchido(codigo)) && (!exigeTurma || preenchido(turma));
}

/**
 * Destino de uma fase: a cena dela ou, enquanto a entrega não existe, a tela
 * "Em breve" com o nome da fase.
 */
export function destinoDaFase (fase) {
    const alvo = FASES[fase];
    if (!alvo) return null;
    return alvo.cena
        ? { cena: alvo.cena, dados: {} }
        : { cena: SCENES.EM_BREVE, dados: { titulo: alvo.nome } };
}

/** `DP-TF-005`: o caminho oculto só existe para a sessão de teste. */
export function faseDoAtalho (tecla, sessao) {
    if (!sessao?.teste) return null;
    const letra = String(tecla ?? '').toUpperCase();
    const achada = Object.entries(FASES).find(([, fase]) => fase.tecla === letra);
    return achada ? achada[0] : null;
}

/**
 * Estado dos três botões do Aguarde.
 * `RI-AGU-01`: Insights e Materiais ficam visíveis, porém inativos, quando
 * indisponíveis. `RI-AGU-02`: o Ranking segue a mesma regra — aparece inativo até o
 * facilitador habilitar (antes ficava oculto; pedido do cliente em 03/10/2026).
 */
export function botoesDoAguarde (estado = {}) {
    return {
        insights: { visivel: true, ativo: Boolean(estado.insightsDisponivel) },
        materiais: { visivel: true, ativo: Boolean(estado.materiaisDisponivel) },
        ranking: { visivel: true, ativo: Boolean(estado.rankingHabilitado) }
    };
}

/** `RN-AGU-01`: texto do estado intermediário entre atividades. */
export function mensagemDoAguarde ({ concluidas = 0, total = 5 } = {}) {
    if (concluidas >= total) return t('aguarde.todas_concluidas');
    return concluidas === 0
        ? t('aguarde.primeira')
        : t('aguarde.proxima');
}
